import { AlertCircle, CheckCircle2 } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

export type RecipeTab =
  | "general"
  | "availability"
  | "ingredients"
  | "execution"
  | "containers"
  | "shelfLife"
  | "labeling"
  | "cost";

export type TabCompletionStatus = "complete" | "incomplete" | "empty";

interface TabConfig {
  id: RecipeTab;
  label: string;
  status: TabCompletionStatus;
}

interface RecipeTabNavProps {
  activeTab: RecipeTab;
  onTabChange: (tab: RecipeTab) => void;
  tabStatuses?: Partial<Record<RecipeTab, TabCompletionStatus>>;
  showStatusIcons?: boolean;
}

export function RecipeTabNav({
  activeTab,
  onTabChange,
  tabStatuses = {},
  showStatusIcons = true,
}: RecipeTabNavProps) {
  const { t } = useLingui();

  const tabs: TabConfig[] = [
    { id: "general", label: t`General Details`, status: tabStatuses.general ?? "incomplete" },
    { id: "availability", label: t`Availability by Restaurant or Region`, status: tabStatuses.availability ?? "complete" },
    { id: "ingredients", label: t`Ingredients`, status: tabStatuses.ingredients ?? "incomplete" },
    { id: "execution", label: t`Execution Steps`, status: tabStatuses.execution ?? "incomplete" },
    { id: "containers", label: t`Storage Containers & Tools`, status: tabStatuses.containers ?? "incomplete" },
    { id: "shelfLife", label: t`Shelf Life & Storage`, status: tabStatuses.shelfLife ?? "incomplete" },
    { id: "labeling", label: t`Labeling`, status: tabStatuses.labeling ?? "incomplete" },
    { id: "cost", label: t`Cost & Financials`, status: tabStatuses.cost ?? "incomplete" },
  ];

  return (
    <div className="flex items-center gap-1 overflow-x-auto rounded-[6px] bg-(--color-surface-muted) p-1">
      {tabs.map((tab) => {
        const isActive = tab.id === activeTab;
        const StatusIcon = tab.status === "complete" ? CheckCircle2 : AlertCircle;
        const iconColor =
          tab.status === "complete"
            ? "text-(--color-success-text)"
            : "text-(--color-warning-text)";

        return (
          <Button
            key={tab.id}
            variant="ghost"
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onTabChange(tab.id)}
            className={cn(
              "flex shrink-0 items-center gap-1 rounded-[6px] px-3 py-2 text-sm transition-colors whitespace-nowrap",
              isActive
                ? "bg-(--color-surface-content) font-medium text-(--color-text-primary) shadow-xs hover:bg-(--color-surface-content)"
                : "font-normal text-(--color-text-secondary) hover:text-(--color-text-primary) hover:bg-transparent",
            )}
          >
            {showStatusIcons && <StatusIcon className={cn("size-5 shrink-0", iconColor)} />}
            {tab.label}
          </Button>
        );
      })}
    </div>
  );
}
